import { validatePhoneNumber } from '../../util/ValidationUtils';
import { LocationWizardData } from './types';

/**
 * Map of field key -> human-readable error message for a single wizard step.
 * An empty map means the step has no validation errors.
 */
export type StepErrors = Record<string, string>;

/**
 * Field keys used for inline required-field validation in each step. Keeping
 * them as shared constants avoids typos between the validator and the step UI.
 */
export const FIELD = {
  locationName: 'locationName',
  locationAddress: 'locationAddress',
  phoneNumber: 'phoneNumber',
  locationDescription: 'locationDescription',
  totalNumberOfSpaces: 'totalNumberOfSpaces',
  managedSpaces: 'managedSpaces',
  pricePerDay: 'pricePerDay',
  pricePerMonth: 'pricePerMonth',
  locationEquipmentTypes: 'locationEquipmentTypes',
} as const;

/**
 * Returns true when the wizard data has a complete, geocoded address
 * (street, city, state, zip + coordinates). The ZIP is waived for an address
 * the supplier manually confirmed: Google returns no `postal_code` for a
 * route-level match, so requiring one would permanently block the very
 * real-world addresses the confirmation flow exists to allow.
 */
const hasCompleteAddress = (data: LocationWizardData): boolean =>
  Boolean(
    data.locationAddress &&
      data.locationAddress.trim() &&
      data.addressLatitude != null &&
      data.addressLongitude != null &&
      data.addressLine1 &&
      data.city &&
      data.state &&
      (data.zip || data.addressManuallyConfirmed)
  );

/**
 * Returns true when the ZIP is the *only* thing missing from an otherwise
 * resolved address. Google omits `postal_code` from route-level matches, so a
 * supplier who accepted Google's own suggestion can land here having done
 * nothing wrong — telling them to "check for typos or select a suggestion
 * from the dropdown" would send them back through the exact path that just
 * failed. They're asked for the one thing that's actually missing instead.
 */
const isMissingOnlyZip = (data: LocationWizardData): boolean =>
  Boolean(
    !data.zip &&
      !data.addressManuallyConfirmed &&
      data.addressLatitude != null &&
      data.addressLongitude != null &&
      data.addressLine1 &&
      data.city &&
      data.state
  );

/** Validates Step 1 (Info). */
const validateInfoStep = (data: LocationWizardData): StepErrors => {
  const errors: StepErrors = {};

  if (!data.locationName || !data.locationName.trim()) {
    errors[FIELD.locationName] = 'Please enter a location name.';
  }

  if (!data.locationAddress || !data.locationAddress.trim()) {
    errors[FIELD.locationAddress] = 'Please enter a location address.';
  } else if (!hasCompleteAddress(data)) {
    errors[FIELD.locationAddress] = isMissingOnlyZip(data)
      ? 'Please include a ZIP code in your address.'
      : 'Address could not be verified. Please check for typos or select a suggestion from the dropdown.';
  }

  if (!data.phoneNumber || !data.phoneNumber.trim()) {
    errors[FIELD.phoneNumber] = 'Please enter a phone number.';
  } else if (!validatePhoneNumber(data.phoneNumber, true)) {
    errors[FIELD.phoneNumber] = 'Please enter a valid 10-digit phone number.';
  }

  return errors;
};

/** Validates Step 2 (Location Settings). */
const validateSettingsStep = (data: LocationWizardData): StepErrors => {
  const errors: StepErrors = {};

  if (!data.locationDescription || !data.locationDescription.trim()) {
    errors[FIELD.locationDescription] = 'Please enter a location description.';
  }

  if (!data.totalNumberOfSpaces || data.totalNumberOfSpaces <= 0) {
    errors[FIELD.totalNumberOfSpaces] = 'Please enter your location capacity (greater than zero).';
  }

  // When automated approvals are enabled, the number of auto-approve spaces must
  // be greater than zero and cannot exceed the total location capacity.
  if (data.managedLocation) {
    if (!data.managedSpaces || data.managedSpaces <= 0) {
      errors[FIELD.managedSpaces] = 'Please enter the number of auto-approve spaces (greater than zero).';
    } else if (
      data.totalNumberOfSpaces != null &&
      data.totalNumberOfSpaces > 0 &&
      data.managedSpaces > data.totalNumberOfSpaces
    ) {
      errors[FIELD.managedSpaces] = 'Auto-approve spaces cannot exceed your location capacity.';
    }
  }

  return errors;
};

/** Validates Step 5 (Pricing). */
const validatePricingStep = (data: LocationWizardData): StepErrors => {
  const errors: StepErrors = {};

  // Auto-approve (managed) locations require daily bookings, so a non-null,
  // > $0 daily price is mandatory when managedLocation is enabled.
  if (data.managedLocation) {
    if (data.pricePerDay === null || data.pricePerDay <= 0) {
      errors[FIELD.pricePerDay] = 'Please enter a daily price greater than $0.';
    }
  } else if (data.pricePerDay !== null && data.pricePerDay <= 0) {
    // When daily bookings are optionally enabled (pricePerDay is non-null), a
    // daily price greater than $0 is still required.
    errors[FIELD.pricePerDay] = 'Please enter a daily price greater than $0.';
  }

  // The monthly rate is optional (matches production): a location can be saved
  // and submitted for review with a blank monthly rate, in which case the daily
  // rate is applied to monthly bookings. At least one valid rate must exist, so
  // when the monthly rate is blank a valid daily rate is required.
  const hasMonthlyRate = data.pricePerMonth != null && data.pricePerMonth > 0;
  const hasDailyRate = data.pricePerDay != null && data.pricePerDay > 0;
  if (!hasMonthlyRate && !hasDailyRate) {
    errors[FIELD.pricePerMonth] =
      'Please enter a monthly price, or a daily price that will apply to monthly bookings.';
  }

  // At least one accepted equipment type is required, and every added row must
  // have an equipment type selected. Prices are optional (blank = default rate).
  if (!data.locationEquipmentTypes || data.locationEquipmentTypes.length === 0) {
    errors[FIELD.locationEquipmentTypes] =
      'Please add at least one equipment type your yard accepts.';
  } else if (data.locationEquipmentTypes.some((e) => !e.equipmentType)) {
    errors[FIELD.locationEquipmentTypes] =
      'Please select an equipment type for each row you added.';
  }

  return errors;
};

/**
 * Runs client-side required-field validation for the given wizard step index.
 * Returns a map of field -> message (empty when the step is valid).
 *
 * Steps 3 (Manager) and 4 (Features) have no required fields.
 */
export const validateStep = (stepIndex: number, data: LocationWizardData): StepErrors => {
  switch (stepIndex) {
    case 0:
      return validateInfoStep(data);
    case 1:
      return validateSettingsStep(data);
    case 4:
      return validatePricingStep(data);
    default:
      return {};
  }
};
